// useSmsSimulator — Simulator & order-lookup state, ported from the prototype's
// sms-simulator.js Alpine module. runSimulation builds a plain results object
// that the template renders with v-for.
import { ref, computed, nextTick } from "vue";
import * as SMS from "../sms-config";

export function useSmsSimulator() {
  // ── Scenario inputs (v-model bound) ───────────────────────────────────
  const simType = ref("driver");
  const simBuilding = ref("house");
  const simWindow = ref("morning");
  const simWeather = ref("perfect");
  const simConsent = ref("yes");
  const simTiming = ref("before");
  const simVersion = ref("live");
  const simName = ref("Sarah Chen");

  // ── Run state ─────────────────────────────────────────────────────────
  const simRunning = ref(false);
  const simResults = ref(null); // { summary, countLabel, rows: [...] }
  const simResultsEl = ref(null); // DOM ref for scroll-into-view

  // ── Order lookup state ────────────────────────────────────────────────
  const orderInput = ref("");
  const orderResult = ref(null);
  const orderError = ref(false);
  const orderApplied = ref(false);
  const lastOrder = ref(null);

  // Order-lookup result as display rows.
  const orderFields = computed(() => {
    const r = orderResult.value;
    if (!r) return [];
    return [
      { label: "Recipient", value: r.recipient },
      { label: "Delivery type", value: r.type },
      { label: "Building", value: r.building },
      { label: "Window", value: r.window },
      { label: "Consented", value: r.consent },
      { label: "Order placed", value: r.timing }
    ];
  });

  // ── Pure-ish builder: read current inputs → results object ────────────
  function computeSim() {
    const name = simName.value || "Sarah Chen";
    const windowLabel = SMS.WINDOW_LABELS[simWindow.value];
    const buildingLabel =
      SMS.BUILDING_LABELS[simBuilding.value] || simBuilding.value;
    const weatherLabel = SMS.WEATHER_LABELS[simWeather.value];
    const typeLabel = simType.value === "driver" ? "Callia Driver" : "Courier";
    const winCap = SMS.capitalize(simWindow.value);
    const weatherNote = SMS.WEATHER_NOTES[simWeather.value];
    const dropNote = SMS.DROP_NOTES[simBuilding.value];

    // Batch assignment (exactly one heads-up batch per order).
    const batch1 = simConsent.value === "yes" && simTiming.value === "before";
    const batch3 = simTiming.value === "after-b2";

    const todayTomorrow = batch1 ? "tomorrow" : "today";
    const batchLabel = batch1 ? "Batch 1" : batch3 ? "Batch 3" : "Batch 2";
    const huTriggerLabel = batch1
      ? "Batch 1 · 10:00 AM day before"
      : batch3
      ? "Batch 3 · At order pull"
      : "Batch 2 · 8:00 AM delivery day";

    const huContent =
      simBuilding.value === "apartment"
        ? `Hi ${name}, your Callia flowers are arriving ${todayTomorrow}! 🌸 We'll be at your building between ${windowLabel}. Please ensure lobby access or reply with your buzzer code. ${weatherNote}`
        : simBuilding.value === "business"
        ? `Hi ${name}, a Callia delivery is arriving ${todayTomorrow} between ${windowLabel}. 🌷 We'll drop it with reception. ${weatherNote}`
        : simBuilding.value === "hospital"
        ? `Hi ${name}, a beautiful Callia delivery is heading your way ${todayTomorrow}! 🌷 Arriving between ${windowLabel} — our driver will check in at reception. ${weatherNote}`
        : `Hi ${name}, your Callia flowers are arriving ${todayTomorrow}! 🌷 We'll be at your door between ${windowLabel}. ${weatherNote}`;

    const etaContent = `Hi ${name}, your Callia driver is on the way! 🚗 Estimated arrival: ${windowLabel}.`;
    const deliveredContent = `We've delivered your Callia box! ${SMS.capitalize(
      dropNote
    )}. Here is a quick peek: [photo link]. Check out these tips to help your flowers last! [care guide]`;
    const deliveredSenderContent = `Your Callia flowers were delivered to ${name}! 🌷 ${SMS.capitalize(
      dropNote
    )}. We hope they love them!`;

    const etaTime =
      simWindow.value === "morning"
        ? "~9:45 AM"
        : simWindow.value === "afternoon"
        ? "~1:30 PM"
        : "~5:45 PM";
    const delTime =
      simWindow.value === "morning"
        ? "~11:20 AM"
        : simWindow.value === "afternoon"
        ? "~3:40 PM"
        : "~7:15 PM";

    let count = 2;
    if (simType.value === "driver") count++;
    count += 2;

    const variationMeta = `Variation: ${buildingLabel} · ${winCap} · ${weatherLabel}`;
    const rows = [];

    // 1) Day-before (Batch 1)
    if (batch1) {
      rows.push({
        id: "dayprior",
        active: true,
        senderTone: false,
        time: { main: "10:00 AM", sub: "Day before" },
        bubble: huContent,
        meta: ["📨 Heads-up", huTriggerLabel, variationMeta, "1 segment"]
      });
    } else {
      const skipReason =
        simConsent.value === "no"
          ? "did not consent at checkout"
          : "placed after 10:00 AM day-before cutoff";
      rows.push({
        id: "dayprior",
        active: false,
        bubble: `Heads-up (Batch 1) — not sent: ${skipReason}`,
        metaNote: "Condition not met · Will not send"
      });
    }

    // 2) Day-of (Batch 2/3)
    if (batch1) {
      rows.push({
        id: "dayof",
        active: false,
        bubble:
          "Heads-up (Batch 2 / day-of) — not sent: recipient captured in Batch 1",
        metaNote: "Condition not met · Will not send"
      });
    } else {
      rows.push({
        id: "dayof",
        active: true,
        senderTone: false,
        time: { main: batch3 ? "Order pull" : "8:00 AM", sub: "Delivery day" },
        bubble: huContent,
        meta: ["📨 Heads-up", huTriggerLabel, variationMeta, "1 segment"]
      });
    }

    // 3) ETA (driver only)
    if (simType.value === "driver") {
      rows.push({
        id: "eta",
        active: true,
        senderTone: false,
        time: { main: etaTime, sub: "Delivery day" },
        bubble: etaContent,
        meta: [
          "📨 ETA",
          "Trigger: Onfleet route started",
          `Variation: ${buildingLabel} · ${winCap}`,
          "1 segment"
        ]
      });
    } else {
      rows.push({
        id: "eta",
        active: false,
        bubble: "ETA — not sent: couriers do not send ETA messages",
        metaNote: "Not applicable for courier delivery"
      });
    }

    // 4) Delivered — Recipient
    rows.push({
      id: "delivered",
      active: true,
      senderTone: false,
      time: { main: delTime, sub: "Delivery day" },
      bubble: deliveredContent,
      meta: ["📨 Delivered — Recipient", "Trigger: delivery scan", "1 segment"]
    });

    // 5) Delivered — Sender
    rows.push({
      id: "delivered-sender",
      active: true,
      senderTone: true,
      time: { main: delTime, sub: "Delivery day" },
      bubble: deliveredSenderContent,
      meta: ["📨 Delivered — Sender", "Trigger: delivery scan", "1 segment"]
    });

    // 6) 3-day follow-up
    rows.push({
      id: "followup",
      active: true,
      senderTone: false,
      time: { main: delTime, sub: "+3 days" },
      bubble: `Hi ${name}! 🌷 Hope you're still enjoying your Callia flowers. How are they looking? We'd love to hear — reply anytime!`,
      meta: [
        "📨 3-day follow-up",
        "Trigger: +3 days post-delivery",
        "1 segment"
      ]
    });

    return {
      summary: `${typeLabel} · ${buildingLabel} · ${winCap} · ${weatherLabel} · ${batchLabel}`,
      countLabel: `${count} messages would send`,
      rows
    };
  }

  // Seed the default scenario so the Results card is never blank on load.
  function initSimulator() {
    simResults.value = computeSim();
  }

  function runSimulation() {
    simRunning.value = true;
    setTimeout(() => {
      simResults.value = computeSim();
      simRunning.value = false;
      nextTick(() => {
        const el = simResultsEl.value;
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }, 700);
  }

  // ── Order lookup ──────────────────────────────────────────────────────
  function lookupOrder() {
    const raw = (orderInput.value || "").trim().replace(/^#/, "");
    const order = SMS.MOCK_ORDERS[raw];
    if (!order) {
      orderResult.value = null;
      orderError.value = true;
      lastOrder.value = null;
      return;
    }
    orderError.value = false;
    lastOrder.value = order;
    const timingLabel =
      order.timing === "before"
        ? "Before cutoff (Batch 1)"
        : order.timing === "after-b2"
        ? "After 8AM day-of (Batch 3)"
        : "After 10AM day-before (Batch 2)";
    orderResult.value = {
      title: "Order " + order.title,
      recipient: order.recipient,
      type: order.type === "driver" ? "Callia Driver" : "Courier",
      building: SMS.BUILDING_LABELS[order.building] || order.building,
      window: order.windowLabel,
      consent: order.consent === "yes" ? "Yes" : "No",
      timing: timingLabel
    };
  }

  let appliedTimer;
  function applyOrderToScenario() {
    if (!lastOrder.value) return;
    const o = lastOrder.value;
    simType.value = o.type;
    simBuilding.value = o.building;
    simWindow.value = o.window;
    simConsent.value = o.consent;
    simTiming.value = o.timing;
    simName.value = o.recipient;
    orderApplied.value = true;
    clearTimeout(appliedTimer);
    appliedTimer = setTimeout(() => {
      orderApplied.value = false;
    }, 1500);
  }

  function clearOrderLookup() {
    orderResult.value = null;
    orderError.value = false;
  }

  return {
    simType,
    simBuilding,
    simWindow,
    simWeather,
    simConsent,
    simTiming,
    simVersion,
    simName,
    simRunning,
    simResults,
    simResultsEl,
    orderInput,
    orderResult,
    orderError,
    orderApplied,
    lastOrder,
    orderFields,
    initSimulator,
    runSimulation,
    lookupOrder,
    applyOrderToScenario,
    clearOrderLookup
  };
}
